import { Api } from "@relode/core";

import { SearchResults } from "../../../../features/candidates/_infra/search";
import {
    CandidateInterviewRequest,
    CandidateOfferRequest,
    CandidateSearchRequest,
    HireRequest,
    CandidateShareResponse,
    LeadJobDetailResponse,
    CandidateInterviewSendNotificationRequest,
} from ".";

const function_name = (name: string) =>
    `client.backend.services.candidates.${name}`;

// R.Client.Api.Controllers.Candidates
export const candidateService = {
    search: async (
        criteria: CandidateSearchRequest,
        signal?: AbortSignal
    ): Promise<SearchResults> => {
        const url = "/candidates";
        const result = await Api.url(url)
            .body({
                ...criteria,
            })
            .retries(3)
            .useSignal(signal)
            .throwOnAbort()
            .track({ name: function_name("search"), data: { criteria } })
            .post<SearchResults>();
        return result;
    },
    getCandidateDetails: async (
        jobId: number,
        leadId: number,
        signal?: AbortSignal
    ): Promise<LeadJobDetailResponse> => {
        const url = `/candidates/${jobId}/${leadId}`;
        const result = await Api.url(url)
            .retries(3)
            .throwOnAbort()
            .useSignal(signal)
            .track({
                name: function_name("getCandidateDetails"),
                data: { jobId, leadId },
            })
            .get<LeadJobDetailResponse>();
        return result;
    },
    reject: async (
        jobId: number,
        leadId: number,
        reasontype: number,
        reason: string,
        hiredEndDate: string,
        candidateConcurrencyToken: string,
        signal?: AbortSignal
    ): Promise<void> => {
        const url = `/candidates/${jobId}/${leadId}/reject`;
        const result = await Api.url(url)
            .body({
                reasontype,
                reason,
                hiredEndDate,
                candidateConcurrencyToken,
            })
            .throwOnAbort()
            .useSignal(signal)
            .track({
                name: function_name("rejectCandidate"),
                data: { jobId, leadId },
            })
            .put<void>();
        return result;
    },

    interview: async (
        leadId: number,
        jobId: number,
        interviewRequest: CandidateInterviewRequest,
        signal?: AbortSignal
    ): Promise<void> => {
        const url = `/candidates/${jobId}/${leadId}/interview`;
        const result = await Api.url(url)
            .body({
                ...interviewRequest,
            })
            .throwOnAbort()
            .useSignal(signal)
            .track({
                name: function_name("interview"),
                data: { jobId, leadId },
            })
            .put<void>();
        return result;
    },

    editInterviewDetails: async (
        leadId: number,
        jobId: number,
        interviewRequest: CandidateInterviewRequest,
        signal?: AbortSignal
    ): Promise<void> => {
        const url = `/candidates/${jobId}/${leadId}/interview/details/${interviewRequest.candidateInterviewId}`;
        const result = await Api.url(url)
            .body({
                ...interviewRequest,
            })
            .throwOnAbort()
            .useSignal(signal)
            .track({
                name: function_name("editInterviewDetails"),
                data: { jobId, leadId },
            })
            .put<void>();
        return result;
    },

    sendInterviewNotification: async (
        leadId: number,
        jobId: number,
        interviewId: number,
        request: CandidateInterviewSendNotificationRequest
    ): Promise<void> => {
        const url = `/candidates/${jobId}/${leadId}/interview/${interviewId}/notification`;
        await Api.url(url)
            .retries(0)
            .useSignal()
            .throwOnAbort()
            .track({
                name: function_name("sendInterviewNotification"),
                data: { leadId, jobId, interviewId, request },
            })
            .post(request);
    },

    addNewInterview: async (
        leadId: number,
        jobId: number,
        interviewRequest: CandidateInterviewRequest,
        signal?: AbortSignal
    ): Promise<void> => {
        const url = `/candidates/${jobId}/${leadId}/interview/details`;
        const result = await Api.url(url)
            .body({
                ...interviewRequest,
            })
            .throwOnAbort()
            .useSignal(signal)
            .track({
                name: function_name("addNewInterview"),
                data: { jobId, leadId },
            })
            .post<void>();
        return result;
    },

    offer: async (
        leadId: number,
        jobId: number,
        candidateOfferRequest: CandidateOfferRequest,
        signal?: AbortSignal
    ): Promise<void> => {
        const url = `/candidates/${jobId}/${leadId}/offer`;
        const result = await Api.url(url)
            .body({ ...candidateOfferRequest })
            .throwOnAbort()
            .useSignal(signal)
            .track({ name: function_name("offer"), data: { jobId, leadId } })
            .put<void>();
        return result;
    },

    hire: async (
        jobId: number,
        leadId: number,
        hireRequest: HireRequest,
        signal?: AbortSignal
    ): Promise<void> => {
        const url = `/candidates/${jobId}/${leadId}/hire`;
        const result = await Api.url(url)
            .body({ ...hireRequest })
            .throwOnAbort()
            .useSignal(signal)
            .track({ name: function_name("hire"), data: { jobId, leadId } })
            .put<void>();
        return result;
    },

    editHireDetails: async (
        jobId: number,
        leadId: number,
        request: HireRequest,
        signal?: AbortSignal
    ): Promise<void> => {
        const url = `/candidates/${jobId}/${leadId}/hire/details`;
        const result = await Api.url(url)
            .body({ ...request })
            .throwOnAbort()
            .useSignal(signal)
            .track({
                name: function_name("hireDetails"),
                data: { jobId, leadId },
            })
            .put<void>();
        return result;
    },

    getCandidateShareLink: async (
        jobId: number,
        leadId: number,
        signal?: AbortSignal
    ): Promise<CandidateShareResponse> => {
        const url = `/candidates/${jobId}/${leadId}/share`;
        const result = await Api.url(url)
            .retries(3)
            .throwOnAbort()
            .useSignal(signal)
            .track({
                name: function_name("getCandidateShareLink"),
                data: { jobId, leadId },
            })
            .post<CandidateShareResponse>();
        return result;
    },
};
